import {
    useEffect,
    useState,
    type FC
} from "react";
import {
    type FocusEvent,
    Pressable
} from "react-native";
import type ISidebarInputProps from "./type";
import type {
    SidebarInputButtonProps
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../../../types";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useSidebarRootContext,
    useSidebarContext
} from "../../context";
import type {
    SidebarWebPropsType
} from "../../type";
import {
    Search as SearchIcon
} from "lucide-react-native";
import {
    getSidebarRowRadius,
    getSidebarMeasures
} from "../../stylesheet";
import {
    warnOnce
} from "../../../../utils/development";
import {
    getSidebarOverlaySide
} from "../../util";
import InputGroup from "../../../inputGroup";
import StateLayer from "../../../stateLayer";
import Tooltip from "../../../tooltip";
import Kbd from "../../../kbd";

const SidebarInputIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <SearchIcon
    color={customColor}
    size={size}
/>;

const SidebarInputButton: FC<SidebarInputButtonProps> = ({
    customTheme: customThemeProp,
    icon: IconComponent,
    onPress,
    style,
    label
}) => {
    const root = useSidebarRootContext({
        part: "Sidebar.Input"
    });

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({});

    const measures = getSidebarMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    });

    const currentRadius = getSidebarRowRadius({
        variant: root?.variant ?? "sidebar",
        measures,
        radiuses,
        borders
    });

    const {
        button: buttonDynamicStyle
    } = useStyles({
        controlHeights,
        isFocusVisible,
        currentRadius,
        shapeConfigs,
        borders,
        colors
    });

    const webProps: SidebarWebPropsType = {
        dataSet: interactionHandlers.dataSet
    };

    return <Tooltip
        side={getSidebarOverlaySide({
            side: root?.side ?? "left",
            direction
        })}
        customTheme={customTheme}
        title={label}
    >
        <Pressable
            {...interactionHandlers}
            {...webProps}
            style={[
                style,
                stylesheet.button,
                buttonDynamicStyle
            ]}
            aria-label={label}
            onPress={onPress}
            role="button"
        >
            <StateLayer
                borderRadius={currentRadius}
                customTheme={customTheme}
                overlayColor="default"
                isPressed={isPressed}
                isHovered={isHovered}
                borderWidth={0}
            />
            <IconComponent
                size={iconSizes.medium}
                color="mid"
            />
        </Pressable>
    </Tooltip>;
};

const SidebarInput: FC<ISidebarInputProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    icon = SidebarInputIcon,
    onFocus: onFocusProp,
    placeholder,
    shortcut,
    style,
    ...props
}) => {
    const provider = useSidebarContext({
        part: "Sidebar.Input"
    });

    const root = useSidebarRootContext({
        part: "Sidebar.Input"
    });

    const customLocalize = customLocalizeProp ?? root?.customLocalize;

    const customTheme = customThemeProp ?? root?.customTheme;

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const [
        isFocusRequested,
        setIsFocusRequested
    ] = useState(false);

    const isCollapsed = root?.isCollapsed ?? false;

    const currentPlaceholder = placeholder ?? localize("search");

    const label = props["aria-label"] ?? currentPlaceholder;

    const isNamed = !!props["aria-label"] || !!props["aria-labelledby"];

    useEffect(() => {
        if(isNamed) {
            return;
        }

        warnOnce({
            message: "Sidebar.Input needs an accessible name. Pass aria-label."
        });
    }, [
        isNamed
    ]);

    const handleFocus = (event: FocusEvent) => {
        setIsFocusRequested(false);

        onFocusProp?.(event);
    };

    const handleExpand = () => {
        setIsFocusRequested(true);

        provider?.handleOpenChange(true);
    };

    if(isCollapsed) {
        return <SidebarInputButton
            customTheme={customTheme}
            onPress={handleExpand}
            style={style}
            label={label}
            icon={icon}
        />;
    }

    return <InputGroup
        customLocalize={customLocalize}
        spreadBehaviour="stretch"
        customTheme={customTheme}
        style={style}
        size="small"
    >
        <InputGroup.Addon
            icon={icon}
        />
        <InputGroup.Input
            {...props}
            autoFocus={isFocusRequested || props.autoFocus}
            placeholder={currentPlaceholder}
            returnKeyType="search"
            onFocus={handleFocus}
            aria-label={label}
            inputMode="search"
        />
        {
            !!shortcut && <InputGroup.Addon
                align="end"
            >
                <Kbd
                    keys={shortcut}
                />
            </InputGroup.Addon>
        }
    </InputGroup>;
};
export default SidebarInput;
